import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import axios from '../../../services/axios';

// ─── Estilos locais ───────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  margin: 0;
  color: #111;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
`;

const ModalFooter = styled.div`
  padding: 12px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 12px;
`;

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const ProviderItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
`;

const ProviderItemName = styled.p`
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  color: #111;
`;

const ProviderItemMeta = styled.p`
  font-size: 11px;
  color: #888;
  margin: 2px 0 0;
`;

const TypeBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(25, 118, 210, 0.1);
  color: #1565c0;
  border: 1px solid rgba(25, 118, 210, 0.25);
  flex-shrink: 0;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
`;

const Divider = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin: 12px 0;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    font-weight: 600;
    color: #444;
  }

  input, select {
    width: 100%;
    box-sizing: border-box;
  }
`;

// ─── Constantes ───────────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 10;

// ─── Componente ───────────────────────────────────────────────────────────────
export default function ServiceAquisitionProviderPickerModal({ isOpen, onSelect, onClose }) {
  // ─── Estado de busca/listagem ─────────────────────────────────────────────
  const [searchTerm,  setSearchTerm]  = useState('');
  const [providers,   setProviders]   = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);

  // ─── Estado do formulário de criação ─────────────────────────────────────
  const [isCreating,      setIsCreating]      = useState(false);
  const [newProviderName, setNewProviderName] = useState('');
  const [newProviderType, setNewProviderType] = useState('');
  const [newCnpjNumber,   setNewCnpjNumber]   = useState('');
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);

  // ─── Busca prestadores na API ─────────────────────────────────────────────
  const fetchProviders = useCallback(async (search = '', page = 1) => {
    setIsSearching(true);
    try {
      const params = { limit: ITEMS_PER_PAGE, page };
      if (search.trim()) params.search = search.trim();

      const response = await axios.get('/service-aquisition/providers', { params });
      setProviders(response.data.data.providers);
      setTotalPages(response.data.meta.total_pages);
      setCurrentPage(page);
    } catch (err) {
      toast.error('Erro ao buscar prestadores');
    } finally {
      setIsSearching(false);
    }
  }, []);

  // ─── Carrega ao abrir — executa UMA única vez, sem double loading ─────────
  useEffect(() => {
    if (!isOpen) return;

    // Reseta estado ao abrir
    setSearchTerm('');
    setIsCreating(false);
    setNewProviderName('');
    setNewProviderType('');
    setNewCnpjNumber('');
    setCurrentPage(1);
    setTotalPages(1);

    fetchProviders('', 1);
  // fetchProviders é estável (useCallback sem deps) — seguro incluir
  }, [isOpen, fetchProviders]);

  // ─── Debounce da busca — só dispara quando searchTerm muda após abertura ──
  useEffect(() => {
    if (!isOpen) return undefined;

    // Não dispara na abertura — o useEffect do isOpen já fez a busca inicial
    const debounceTimer = setTimeout(() => {
      fetchProviders(searchTerm, 1);
    }, 350);

    return () => clearTimeout(debounceTimer);
  // Intencionalmente excluindo isOpen e fetchProviders para este effect
  // só reagir a mudanças no searchTerm
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // ─── Troca de página ──────────────────────────────────────────────────────
  function handlePageChange(newPage) {
    fetchProviders(searchTerm, newPage);
  }

  // ─── Seleção de prestador ─────────────────────────────────────────────────
  function handleProviderSelect(provider) {
    onSelect(provider);
    onClose();
  }

  // ─── Criação de novo prestador ────────────────────────────────────────────
  async function handleCreateNewProvider() {
    if (!newProviderName.trim()) { toast.error('Nome do prestador é obrigatório'); return; }
    if (!newProviderType)        { toast.error('Tipo do prestador é obrigatório'); return; }
    if (newProviderType === 'PJ' && !newCnpjNumber.trim()) {
      toast.error('CNPJ é obrigatório para prestadores do tipo PJ');
      return;
    }

    setIsSubmittingNew(true);
    try {
      const response = await axios.post('/service-aquisition/providers', {
        provider_name: newProviderName.trim(),
        provider_type: newProviderType,
        cnpj_number:   newCnpjNumber.trim() || undefined,
      });

      toast.success('Prestador cadastrado com sucesso');
      onSelect(response.data.data.provider);
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message
        ?? err.response?.data?.errors?.[0]?.message
        ?? 'Erro ao cadastrar prestador';
      toast.error(errorMessage);
    } finally {
      setIsSubmittingNew(false);
    }
  }

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalBox>
        <ModalHeader>
          <ModalTitle>{isCreating ? 'Novo Prestador' : 'Selecionar Prestador'}</ModalTitle>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#666' }}>
            ✕
          </button>
        </ModalHeader>

        <ModalBody>
          {!isCreating ? (
            <>
              {/* ─── Busca ──────────────────────────────────────────────── */}
              <SearchInput
                type="text"
                placeholder="Buscar por nome ou CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />

              {/* ─── Lista ──────────────────────────────────────────────── */}
              <ProviderList>
                {isSearching && (
                  <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Buscando...</p>
                )}
                {!isSearching && providers.length === 0 && (
                  <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Nenhum prestador encontrado.</p>
                )}
                {!isSearching && providers.map((provider) => (
                  <ProviderItem key={provider.id} onClick={() => handleProviderSelect(provider)}>
                    <div>
                      <ProviderItemName>{provider.provider_name}</ProviderItemName>
                      <ProviderItemMeta>{provider.cnpj_number ?? 'Sem CNPJ'}</ProviderItemMeta>
                    </div>
                    <TypeBadge>{provider.provider_type}</TypeBadge>
                  </ProviderItem>
                ))}
              </ProviderList>

              <Divider />

              <button
                type="button"
                onClick={() => setIsCreating(true)}
                style={{ fontSize: 13, width: '100%' }}
              >
                + Cadastrar novo prestador
              </button>
            </>
          ) : (
            <FormSection>
              <FormRow>
                <label htmlFor="new-provider-name">Nome do Prestador *</label>
                <input
                  id="new-provider-name"
                  type="text"
                  placeholder="Razão social ou nome"
                  value={newProviderName}
                  onChange={(e) => setNewProviderName(e.target.value)}
                  disabled={isSubmittingNew}
                />
              </FormRow>

              <FormRow>
                <label htmlFor="new-provider-type">Tipo *</label>
                <select
                  id="new-provider-type"
                  value={newProviderType}
                  onChange={(e) => setNewProviderType(e.target.value)}
                  disabled={isSubmittingNew}
                >
                  <option value="">Selecione...</option>
                  <option value="PJ">PJ — Pessoa Jurídica</option>
                  <option value="PF">PF — Pessoa Física</option>
                  <option value="ESTRANGEIRO">Estrangeiro</option>
                </select>
              </FormRow>

              {newProviderType === 'PJ' && (
                <FormRow>
                  <label htmlFor="new-cnpj-number">CNPJ *</label>
                  <input
                    id="new-cnpj-number"
                    type="text"
                    placeholder="XX.XXX.XXX/XXXX-XX"
                    maxLength={18}
                    value={newCnpjNumber}
                    onChange={(e) => setNewCnpjNumber(e.target.value)}
                    disabled={isSubmittingNew}
                  />
                </FormRow>
              )}
            </FormSection>
          )}
        </ModalBody>

        <ModalFooter>
          {/* ─── Paginação (só exibe na listagem) ───────────────────────── */}
          {!isCreating && totalPages > 1 ? (
            <PaginationRow>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isSearching}
                style={{ padding: '2px 8px', fontSize: 12 }}
              >
                ‹
              </button>
              <span>{currentPage} / {totalPages}</span>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isSearching}
                style={{ padding: '2px 8px', fontSize: 12 }}
              >
                ›
              </button>
            </PaginationRow>
          ) : (
            <div />
          )}

          {/* ─── Ações ──────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 8 }}>
            {isCreating ? (
              <>
                <button type="button" onClick={() => setIsCreating(false)} disabled={isSubmittingNew}>
                  Voltar
                </button>
                <button type="button" onClick={handleCreateNewProvider} disabled={isSubmittingNew}>
                  {isSubmittingNew ? 'Salvando...' : 'Salvar Prestador'}
                </button>
              </>
            ) : (
              <button type="button" onClick={onClose}>Cancelar</button>
            )}
          </div>
        </ModalFooter>
      </ModalBox>
    </ModalOverlay>
  );
}
